<template>
  <c3-chart v-if="loaded" ref="chart" :height="height" :data="chartData" :config="chartConfig" type='area' />
  <v-skeleton-loader v-else type="image" :height="height"/>
</template>

<script>
import C3Chart from '@/components/shared/chart/C3Chart.vue'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import { map, round, get, min, max, some } from 'lodash'
import Moment from 'moment-timezone'

export default {
  name: 'TrailingFleetHealthChart',
  components: {
    C3Chart
  },
  mixins: [
    CurrentUserMixin
  ],
  props: {
    selectedCustomers: {
      type: Array,
      default () {
        return []
      }
    },
    height: {
      type: Number,
      default: 200
    }
  },
  data () {
    return {
      loaded: false,
      fleetHealthData: null
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    /**
     * Expose internal chart fit method so we can redraw on demand
     */
    fit () {
      this.$refs.chart.fit()
    },
    /**
     * Load the trailing fleet health data from the API
     */
    async loadData () {
      this.loaded = false
      try {
        const customerIds = map(this.selectedCustomers, x => x.id)
        const start = Moment.tz(this.customerTimezone).subtract(6, 'months').format()
        const end = Moment.tz(this.customerTimezone).format()
        const result = await this.$httpAdapter.get('/customer-insights/fleet-health-history', {
          params: {
            customer_id: customerIds.join(','),
            start: start,
            end: end
          }
        })
        this.fleetHealthData = get(result.data, 'combined.data')
        this.loaded = true
      } catch (error) {
        this.handleError(error)
      }
    }
  },
  computed: {
    /**
     * Format the chart data needed for the graph.
     * @returns Object
     */
    chartData () {
      const dateColumn = map(this.fleetHealthData, x => x.date)
      dateColumn.unshift('date')
      const healthColumn = map(this.fleetHealthData, x => {
        const total = x.num_healthy + x.num_unhealthy
        if (total > 0) {
          return round(x.num_healthy / total * 100)
        }
        return null
      })
      healthColumn.unshift(this.$t('percentage_of_healthy_vehicles'))
      return {
        x: 'date',
        columns: [
          dateColumn,
          healthColumn
        ]
      }
    },
    /**
     * Set the range for the chart. If any values fall below 60, show the full range, otherwise truncate to 60.
     * @returns {number[]}
     */
    rangeValues () {
      if (some(this.chartData.columns[1], x => x != null && x < 60)) {
        return [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]
      } else {
        return [60, 70, 80, 90, 100]
      }
    },
    /**
     * The chart configuration.
     * @returns Object
     */
    chartConfig () {
      return {
        padding: {
          bottom: -30,
          right: 10
        },
        point: {
          show: false
        },
        legend: {
          hide: true
        },
        axis: {
          x: {
            type: 'timeseries',
            tick: {
              count: 12,
              format: '%Y-%m-%d',
              centered: true
            }
          },
          y: {
            label: {
              text: this.$t('percentage')
            },
            tick: {
              values: this.rangeValues,
              format: function (d) {
                return round(d, 1) + '%'
              },
              culling: true
            },
            min: min(this.rangeValues),
            max: max(this.rangeValues)
          }
        },
        line: {
          connectNull: true
        }
      }
    }
  }
}
</script>

<style scoped>

</style>
